import { join } from '../posix.js';
import { MAX_OUTPUTS_TOTAL, clipEnd } from '../mentor-context.js';
import { CHECK_TIMEOUT_MS, runCheck } from './checks.js';
/**
 * Replace `{{seed.KEY}}` placeholders with values the seed script emitted.
 * Lets a mission refer to values that only exist after seeding, such as
 * the hash of a commit the seed deliberately "lost".
 */
export function applyTemplate(value, seed) {
    if (typeof value === 'string') {
        return value.replace(/\{\{\s*seed\.([A-Za-z0-9_]+)\s*\}\}/g, (_, key) => {
            const v = seed[key];
            if (v === undefined)
                throw new Error(`Mission references {{seed.${key}}} but the seed script did not emit @@${key}=`);
            return v;
        });
    }
    if (Array.isArray(value))
        return value.map((v) => applyTemplate(v, seed));
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value))
            out[k] = applyTemplate(v, seed);
        return out;
    }
    return value;
}
export function makeContext(ws, cwd) {
    return {
        git: (args) => ws.exec(`git ${args}`, { cwd, timeoutMs: 15_000 }),
        exec: (cmd) => ws.exec(cmd, { cwd, timeoutMs: CHECK_TIMEOUT_MS }),
        readFile: (rel) => ws.readFile(join(cwd, rel)),
    };
}
async function gradeObjective(ctx, o, seed, evidence) {
    const spec = applyTemplate({ check: o.check, with: o.with }, seed);
    const outcome = await runCheck(ctx, spec);
    const title = applyTemplate(o.title, seed);
    if (outcome.ok)
        return { id: o.id, title, passed: true, hidden: o.hidden === true };
    // An authored fail sentence describes a tested behaviour. A timeout has
    // tested nothing and must not claim the learner's code has that fault.
    if (outcome.timedOut)
        return { id: o.id, title, passed: false, hidden: o.hidden === true,
            timedOut: true, message: outcome.detail, ...(evidence && !o.hidden ? { output: outcome.output } : {}) };
    /*
     * Failed: say why, in the same words whichever language is on.
     *
     * `fail`, `title` and a diagnostic's `message` are templated here for the
     * reason the check above is: they name the very things the seed chose. A
     * level that seeds a culprit SKU and then says "LT-014 is still in the
     * catalogue" has to get that id from somewhere, and the somewhere is
     * `{{seed.CULPRIT_SKU}}`.
     *
     * This used to be true of `with.detail` alone, because `detail` travels
     * inside the spec and the spec was templated wholesale, while `fail` — the
     * string that *overrides* it — was not. That asymmetry was invisible while
     * there was one language: an author who needed interpolation simply wrote
     * the sentence in `detail` and left `fail` off. It stopped being invisible
     * when the translations arrived, because a sidecar has no `with:` to write
     * into, so every translated failure message has to be a `fail:` — and half
     * of those would have rendered `{{seed.CULPRIT_SKU}}` to the learner.
     *
     * Templating the message rather than teaching the sidecar to reach into
     * `with:` is the version that keeps one rule: a translation is strings, and
     * it never touches the grader's arguments.
     */
    let message = o.fail !== undefined ? applyTemplate(o.fail, seed) : outcome.detail;
    let diagnosed;
    let chosen;
    for (const [i, d] of (o.diagnostics ?? []).entries()) {
        const dspec = applyTemplate({ check: d.check, with: d.with }, seed);
        const r = await runCheck(ctx, dspec);
        if (r.ok) {
            message = applyTemplate(d.message, seed);
            diagnosed = r.output;
            chosen = i;
            break;
        }
    }
    const result = { id: o.id, title, passed: false, hidden: o.hidden === true, message };
    if (chosen !== undefined)
        result.diagnostic = chosen;
    // What the diagnostic that chose the sentence printed, when it printed
    // anything, because it is the evidence for that sentence: l24's "does not
    // load" is a `! node -e "import(…)"` whose stderr names the file, the line
    // and the caret, where the objective's own check only caught a stack. A
    // quiet diagnostic — most are — leaves the objective's own output. Never a
    // hidden objective's, whose checks are the answer key the learner is not
    // shown in any form.
    const output = diagnosed || outcome.output;
    if (evidence && o.hidden !== true && output)
        result.output = output;
    return result;
}
/**
 * Trim the outputs to one ceiling across the whole grade, in author order.
 *
 * The earliest failing objective is usually the one the learner is on — the
 * ones after it are often skipped behind it — so it keeps its output whole and
 * a later one gets what is left, cut from the front so its last lines survive.
 */
function capOutputs(objectives) {
    let room = MAX_OUTPUTS_TOTAL;
    for (const o of objectives) {
        if (o.output === undefined)
            continue;
        if (room <= 0) {
            delete o.output;
            continue;
        }
        o.output = clipEnd(o.output, room);
        room -= o.output.length;
    }
}
export async function gradeMission(mission, ws, seed, options = {}) {
    /*
     * A level this build was not given the checks for, refused before anything
     * is run.
     *
     * A checkpoint's objectives are its answer key — the visible checks and the
     * hidden anti-shortcut ones — and the only grade it has that means anything
     * is taken on a server, against a repository nobody else has
     * (docs/checkpoints.md). So the catalogue ships its objective *titles* and
     * nothing behind them, and this is the line that says what that costs:
     * grading it here is not possible, and the refusal is explicit rather than
     * whatever the shape happens to produce.
     *
     * It guards on the flag and not on the shape for the reason `locked` does.
     * Objectives stripped to titles throw inside `runCheck`, which looks like a
     * defence and is an accident of an unknown check type; the same list
     * emptied instead would pass *vacuously* — `every` over nothing is true —
     * and hand out a checkpoint nobody sat, which is the one failure a
     * credential cannot survive.
     */
    if (mission.graded === 'server') {
        throw new Error(`mission "${mission.id}" is graded on the server: this build carries its objective titles and none of its checks`);
    }
    const evidence = options.evidence === true;
    const startedAt = Date.now();
    const ctx = makeContext(ws, mission.cwd);
    const objectives = [];
    const passedIds = new Set();
    let interrupted = false;
    for (const o of mission.objectives) {
        const unmet = (o.requires ?? []).filter((id) => !passedIds.has(id));
        if (interrupted || unmet.length > 0) {
            objectives.push({ id: o.id, title: applyTemplate(o.title, seed), passed: false, hidden: o.hidden === true, skipped: true });
            continue;
        }
        const result = await gradeObjective(ctx, o, seed, evidence);
        // A timed-out VM command may still occupy the guest until its backstop.
        // Stop here instead of queueing another timeout for every later objective.
        interrupted = result.timedOut === true;
        if (result.passed)
            passedIds.add(o.id);
        objectives.push(result);
    }
    if (!evidence)
        return { passed: objectives.every((o) => o.passed), objectives, gradedAt: Date.now() };
    capOutputs(objectives);
    return { passed: objectives.every((o) => o.passed), objectives, gradedAt: Date.now(), evidence: true, startedAt };
}
/**
 * The diagnostics that chose a sentence in this grade, keyed by objective id,
 * for the server-graded verdict of a checkpoint (docs/checkpoints.md, "How a
 * learner checks one").
 *
 * Failed **visible** objectives only. A hidden objective's diagnostics are
 * part of its answer key, and "which of them fired" is an answer to it in the
 * same way the id would be. A skipped one never ran. What comes back is an
 * index and nothing else: the Worker holds the messages and serves one by
 * position, so no check, no output and no seed value ever rides on a verdict.
 * One rule, here, so the Docker runner and the Cloudflare one cannot disagree
 * about what a learner is told.
 */
export function verdictDiagnoses(objectives) {
    const out = {};
    for (const o of objectives) {
        if (!o.passed && !o.skipped && !o.hidden && o.diagnostic !== undefined)
            out[o.id] = o.diagnostic;
    }
    return out;
}
//# sourceMappingURL=index.js.map